import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import { useCallback, useMemo, useState } from "react";
import {
  Box,
  DataTable,
  DataTableGroupByDropdown,
  DataTableToolbar,
  DataTableToolbarActions,
  DataTableToolbarLeft,
  Icon,
  Text,
  UNASSIGNED_GROUP_KEY,
  renderDefaultGroupActions,
  useDataTableState,
  useWidgetRegistry,
  useWidgetSurface,
  type DataTableGroupFieldConfig,
  type DataTableGroupType,
  type SortingState,
  type WidgetInstance,
} from "@salestrak/ui";
import { ExpandableSearch } from "@salestrak/ui";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import { EntityTag } from "#app/components/entity";
import { GroupQueryProvider, useGroupStates } from "#app/lib/hooks/group-query-provider";
import { UpdateAgeDialog } from "#app/components/age/update-age-dialog";
import { ISSUE_STAGE_COLUMN_ID, useIssueColumns } from "#app/components/issues/issue-columns";
import { useIssueTeamsChange } from "#app/components/issues/use-issue-teams-change";
import { WIDGET_DATA_EDIT } from "#app/hooks/issue-view/use-issue-view-mutations";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import {
  buildGroupWhere,
  ISSUE_GROUP_SERVER_KEY,
  type IssueGroupDimension,
} from "#app/components/issues/issue-grouping";
import { useIssueBulkActions } from "#app/components/issues/use-issue-bulk-actions";
import { useIssueView } from "#app/components/issue-view";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import { useIssueListMutations } from "#app/hooks/issue-list/use-issue-list-mutations";
import { useRefetchIssueGroups } from "#app/hooks/issue-list/use-refetch-issue-groups";
import { AnalyticsScope, AnalyticsView } from "#app/lib/analytics";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import { ISSUE_SEARCH_FIELDS } from "#app/lib/filters/list-configs";
import {
  type IssueRow,
  IssuesDocument,
  type SortDirection,
  type StageOption,
  useMemberEntityResolver,
} from "#app/lib/data";
import { buildSearchWhere, combineWhereConditions } from "#app/lib/query-utils";
import { ASSIGNEE_ICON, resolveEntityLabel, type EntityData } from "#app/lib/entity-visuals";
import { AGE_STATUS_CONFIG, type AgeStatus } from "#app/lib/age-status";
// Relative imports (not the `features/dashboards` barrel) to avoid a cycle: the
// barrel re-exports the detail dialog, which renders this panel.
import { DRILLDOWN_ORDER_BY, useWidgetDrilldownWhere } from "./use-widget-drilldown-issues";
import { useDashboardScope } from "./scope/dashboard-scope-context";
import type { WidgetDataSlice } from "./widget-drilldown";
import type { Omittable } from "@salestrak/ui";
import { OutcomePerformanceDataPanel } from "#app/widgets/definitions/outcome-performance/outcome-performance-data-panel";

// The bulk bar's count-chip noun: "1 Project selected".
const PROJECT_SELECTION_NOUN = { one: "Project", other: "Projects" };

/**
 * The "Data" tab of the widget detail dialog: the drill-down into the entities
 * feeding the widget. The data layer ({@link useWidgetDrilldownWhere})
 * reproduces the widget's own query window as ONE folded `where` (a clicked
 * chart element folds in as an exact server predicate), and every table group
 * is its own windowed paged query over it — so the drawer reaches EVERY row
 * feeding the widget, never a capped sample. Grouped mode resolves its group
 * set from the exact server `issueGroupCounts` aggregation, and search is a
 * server predicate over the app-wide issue search columns.
 *
 * Rows render through the real {@link DataTable} with the shared issue columns
 * (no custom fields — the Data tab acts on a drill; building a view with
 * fields is the Projects list's Customize view drawer); the toolbar mirrors
 * the workflow list view (group-by left, search right).
 */

/**
 * The columns a Data tab opens with, by the room it has. Full width (the
 * dialog's Data view): what the row is, where it sits, who and which team
 * holds it, and when it is owed — so a drill into "Unassigned" or a workload
 * bar can be acted on in place (assign, move to a team) without opening each
 * Project. The drawer beside a chart holds only what fits beside the header's
 * picker: the name — whose glyph already carries the stage, as on the
 * Projects list — and the follow up. The other issue columns wait behind
 * the circle-plus picker on the header.
 */
const DEFAULT_VISIBLE_COLUMN_IDS: Record<"wide" | "drawer", ReadonlySet<string>> = {
  wide: new Set(["name", ISSUE_STAGE_COLUMN_ID, "assignees", "teams", "age"]),
  drawer: new Set(["name", "age"]),
};

/** The order the opening columns stand in; the rest follow in the shared
 *  issue-column order. */
const LEADING_COLUMN_ORDER = ["name", ISSUE_STAGE_COLUMN_ID, "assignees", "teams", "age"];

/** The row's selection box and its name never hide. */
const PINNED_COLUMN_IDS = ["select", "name"];

/** Where each viewer's Data-tab columns persist — one choice per layout for
 *  every widget's Data tab, so a column shown once is there on the next
 *  drill, and the narrow drawer never inherits the full view's width. */
const DATA_TABLE_ID: Record<"wide" | "drawer", string> = {
  wide: "widget-data",
  drawer: "widget-data-drawer",
};

// The fields the Data-tab table can group by — exactly the data the drawer's
// rows carry, matching the workflow list view's group-by options.
const GROUP_FIELDS: Record<string, DataTableGroupFieldConfig> = {
  stage: { label: "Stage", icon: PROPERTY_ICON.stage },
  assignees: { label: "Assignee", icon: ASSIGNEE_ICON },
  age: { label: "Follow up", icon: PROPERTY_ICON.followUp },
};

// The drawer's group fields → the shared grouping dimensions (the table's
// `assignees` field key is the shared module's `assignee` dimension).
const FIELD_TO_DIMENSION: Record<string, IssueGroupDimension> = {
  stage: "stage",
  assignees: "assignee",
  age: "age",
};

// Age buckets ordered best→worst, mirroring the board's age pills.
const AGE_GROUP_ORDER: Record<string, number> = {
  onTrack: 0,
  atRisk: 1,
  offTrack: 2,
  disabled: 3,
};

const getIssuesConnection = (data: any) => data?.issues;
const PAGE_LIMIT = 50;

/** What an assignee group whose id the directory cannot answer for is called.
 * The group's total is exact either way, so it names the role rather than
 * asserting a person called "Unknown". */
const PENDING_MEMBER_LABEL = "Member";

/** How to render each group's header — resolved with the group set so the title
 * renderer routes through the entity chip system without re-deriving rows. */
type GroupMeta =
  | { kind: "stage"; stage: StageOption }
  | { kind: "assignee"; entity: EntityData }
  | { kind: "age"; status: AgeStatus }
  | { kind: "unassigned"; label: string };

/** One drill-down group: key/label/meta, exact server total, folded `where`. */
type DrilldownGroupDef = {
  key: string;
  label: string;
  totalCount: number;
  where: Record<string, unknown> | undefined;
  meta: GroupMeta | null;
};

export function WidgetDataPanel({
  instance,
  slice,
  onClearSlice,
  wide = false,
}: Omittable<{
  instance: WidgetInstance;
  /** Chart-element drill-down filter — set when a slice/bar was clicked. */
  slice?: WidgetDataSlice | null;
  onClearSlice?: () => void;
  /** The panel has the dialog's full width (no chart beside it), so its
   *  table opens with the assignment columns. */
  wide?: boolean;
}>) {
  if (instance.type === "outcome-performance") {
    return (
      <OutcomePerformanceDataPanel
        slice={slice?.kind === "outcomeActivity" ? slice : null}
        onClearSlice={onClearSlice}
      />
    );
  }
  return (
    <IssueWidgetDataPanel
      instance={instance}
      slice={slice}
      onClearSlice={onClearSlice}
      layout={wide ? "wide" : "drawer"}
    />
  );
}

function IssueWidgetDataPanel({
  instance,
  slice,
  onClearSlice,
  layout,
}: Omittable<{
  instance: WidgetInstance;
  slice?: WidgetDataSlice | null;
  onClearSlice?: () => void;
  layout: "wide" | "drawer";
}>) {
  // The widget's own data window, reproduced as one folded `where` — a clicked
  // slice is already an exact server predicate inside it.
  const { where: drilldownWhere } = useWidgetDrilldownWhere(instance, slice);
  // The module's declared opening group-by — a key, or a read of the resolved
  // config for a widget whose card is already grouped — honored only when it
  // names one of the drawer's own group fields.
  const registry = useWidgetRegistry();
  const def = registry.get(instance.type);
  const declared = def?.drilldownGroupBy;
  const declaredGroupBy =
    typeof declared === "function"
      ? declared(registry.resolveConfig(def, instance.config, instance.schemaVersion ?? 1))
      : declared;
  const defaultGroupByField =
    declaredGroupBy !== undefined && declaredGroupBy in GROUP_FIELDS ? declaredGroupBy : "none";

  // The surface the detail dialog's panel painted — the table's sticky header
  // and pinned-column masks must paint the same fill.
  const panelSurface = useWidgetSurface();

  // Stage labels resolve from every workspace stage — the window can span
  // workflows, and the Stage column must never render "Unknown" for a row in
  // another workflow.
  const { stages: workspaceStages } = useWorkspaceData();
  // Assignee group keys are whichever members/teams the aggregation counted, so
  // they resolve through the DIRECTORY: the workspace bootstrap page answers for
  // 500 members in name order, which left the alphabet deciding whose group
  // could be named.
  const resolveMember = useMemberEntityResolver();
  // `useWorkspaceData()` stages ARE the stage row — no re-shaping.
  const availableStages: StageOption[] = workspaceStages;
  const stageById = useMemo(() => {
    const map = new Map<string, StageOption>();
    for (const s of availableStages) map.set(s.stageId, s);
    return map;
  }, [availableStages]);

  // Row interactions — the same live cells as the list view: open, stage
  // change, assignees, teams, age dialog. No address handlers → no Address
  // column; no type handler → the Type column reads only.
  const { openIssue } = useIssueView();
  const handleOpenIssue = useCallback(
    (issue: IssueRow) => openIssue(issue, { source: "widget" }),
    [openIssue],
  );
  const { changeStage, updateAssignees } = useIssueListMutations();
  const handleTeamsChange = useIssueTeamsChange(WIDGET_DATA_EDIT);
  const [ageDialogIssue, setAgeDialogIssue] = useState<IssueRow | null>(null);
  const handleAgePress = useCallback((issue: IssueRow) => setAgeDialogIssue(issue), []);
  const refetchGroups = useRefetchIssueGroups();

  // The shared project bulk roster (the list view's): Stage only under a
  // single-workflow dashboard (a cross-workflow selection has no single
  // stage target). Every commit runs on the selected row ids — select-all-
  // by-`where` never engages here.
  const scope = useDashboardScope();
  const analyticsScope =
    scope?.entity?.type === "workflow"
      ? AnalyticsScope.Workflow
      : scope?.entity?.type === "team"
        ? AnalyticsScope.Team
        : AnalyticsScope.Member;
  const scopeWorkflowId = scope?.entity?.type === "workflow" ? scope.entity.id : undefined;
  const bulkActions = useIssueBulkActions({
    view: AnalyticsView.WidgetView,
    scope: analyticsScope,
    workflowId: scopeWorkflowId,
    availableStages,
    refetchGroups,
  });

  const rawColumns = useIssueColumns({
    availableStages,
    onOpenIssue: handleOpenIssue,
    onChangeStage: changeStage,
    onAssigneesChange: updateAssignees,
    onTeamsChange: handleTeamsChange,
    onAgePress: handleAgePress,
  });
  // The assignment columns lead after the row's identity — the Data tab is
  // where a drill gets acted on — then everything else in the shared order.
  const allColumns = useMemo(() => {
    const rank = (c: (typeof rawColumns)[number]) => {
      const index = LEADING_COLUMN_ORDER.indexOf(
        String(c.id ?? (c as { accessorKey?: string }).accessorKey ?? ""),
      );
      return index === -1 ? LEADING_COLUMN_ORDER.length : index;
    };
    return rawColumns
      .map((column, index) => ({ column, index }))
      .sort((a, b) => rank(a.column) - rank(b.column) || a.index - b.index)
      .map(({ column }) => column);
  }, [rawColumns]);
  // Everything past the layout's opening set starts hidden; the viewer's own
  // picks persist under that layout's DATA_TABLE_ID.
  const defaultHiddenColumnIds = useMemo(
    () =>
      rawColumns
        .map((c) => String(c.id ?? (c as { accessorKey?: string }).accessorKey ?? ""))
        .filter((id) => id !== "" && !DEFAULT_VISIBLE_COLUMN_IDS[layout].has(id)),
    [rawColumns, layout],
  );
  const tableView = useTableViewSettings(DATA_TABLE_ID[layout], { defaultHiddenColumnIds });

  const {
    columns,
    groupByField,
    setGroupByField,
    isGrouped,
    groupByLabels,
    groupByIcons,
    groupSortDirection,
    setGroupSortDirection,
  } = useDataTableState({
    columns: allColumns,
    groupFields: GROUP_FIELDS,
    defaultGroupByField,
    defaultGroupSortDirection: "asc",
  });

  // Column-header sort is SERVER ordering: every group's paged query reorders
  // over its whole window, never the loaded page. Unsorted, the drawer keeps
  // the drill-down's default (newest first). Dialog-local — the widget has no
  // saved view to settle a sort into.
  const [sorting, setSorting] = useState<SortingState>([]);
  const orderBy = useMemo<Record<string, SortDirection>>(() => {
    const [primary] = sorting;
    return primary ? { [primary.id]: primary.desc ? "desc" : "asc" } : DRILLDOWN_ORDER_BY;
  }, [sorting]);

  // Search is a SERVER predicate over the app-wide issue search columns —
  // debounced, folded into every group's query, so it narrows the whole
  // window rather than the loaded page.
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const effectiveWhere = useMemo(() => {
    const searchWhere = buildSearchWhere(debouncedSearch, ISSUE_SEARCH_FIELDS);
    return searchWhere
      ? (combineWhereConditions([drilldownWhere, searchWhere]) ?? drilldownWhere)
      : drilldownWhere;
  }, [drilldownWhere, debouncedSearch]);

  // Grouped mode: the group SET comes from the exact server aggregation over
  // the same folded `where`; each non-empty group pages its own windowed query.
  const dimension = isGrouped ? FIELD_TO_DIMENSION[groupByField] : undefined;
  const groupCounts = useIssueGroupCounts({
    where: dimension ? effectiveWhere : undefined,
    groupBy: dimension ? ISSUE_GROUP_SERVER_KEY[dimension] : undefined,
  });
  const groupDefs = useMemo<DrilldownGroupDef[]>(() => {
    if (!dimension) {
      // "Grouped by nothing": one real group (NOT the ungrouped sentinel) so
      // the DataTable keeps the header, count, and collapse affordance.
      return [
        { key: "all", label: "All Projects", totalCount: 0, where: effectiveWhere, meta: null },
      ];
    }
    if (!groupCounts.ready) return [];

    type Sortable = DrilldownGroupDef & { sort: number | string };
    const defs: Sortable[] = Object.entries(groupCounts.counts)
      .filter(([, total]) => total > 0)
      .map(([key, totalCount]) => {
        const where = combineWhereConditions(
          [effectiveWhere, buildGroupWhere(dimension, key)].filter(
            (c): c is Record<string, unknown> => c != null,
          ),
        );
        if (dimension === "stage") {
          const stage = key === UNASSIGNED_GROUP_KEY ? undefined : stageById.get(key);
          return stage
            ? { key, label: stage.name, totalCount, where, meta: { kind: "stage", stage } as const, sort: stage.sortOrder }
            : {
                key,
                label: "No stage",
                totalCount,
                where,
                meta: { kind: "unassigned", label: "No stage" } as const,
                sort: Number.MAX_SAFE_INTEGER,
              };
        }
        if (dimension === "assignee") {
          if (key === UNASSIGNED_GROUP_KEY) {
            // Named assignees sort alphabetically; unassigned last (￿ sorts high).
            return {
              key,
              label: "No assignee",
              totalCount,
              where,
              meta: { kind: "unassigned", label: "No assignee" } as const,
              sort: "￿",
            };
          }
          const member = resolveMember(key);
          const entity: EntityData = member ?? { kind: "user", id: key, user: null };
          const label = member ? resolveEntityLabel(member) : PENDING_MEMBER_LABEL;
          return { key, label, totalCount, where, meta: { kind: "assignee", entity } as const, sort: label };
        }
        // age
        const status = key as AgeStatus;
        return AGE_STATUS_CONFIG[status] && key !== UNASSIGNED_GROUP_KEY
          ? {
              key,
              label: AGE_STATUS_CONFIG[status].label,
              totalCount,
              where,
              meta: { kind: "age", status } as const,
              sort: AGE_GROUP_ORDER[key] ?? 98,
            }
          : {
              key,
              label: "No age",
              totalCount,
              where,
              meta: { kind: "unassigned", label: "No age" } as const,
              sort: 99,
            };
      });

    defs.sort((a, b) => {
      const cmp =
        typeof a.sort === "number" && typeof b.sort === "number"
          ? a.sort - b.sort
          : String(a.sort).localeCompare(String(b.sort));
      return groupSortDirection === "desc" ? -cmp : cmp;
    });
    return defs.map(({ sort: _sort, ...def }) => def);
  }, [
    dimension,
    groupCounts.ready,
    groupCounts.counts,
    effectiveWhere,
    stageById,
    resolveMember,
    groupSortDirection,
  ]);
  const metaByKey = useMemo(() => {
    const map: Record<string, GroupMeta> = {};
    for (const def of groupDefs) if (def.meta) map[def.key] = def.meta;
    return map;
  }, [groupDefs]);

  // Each group pages through its own query provider (mounted below); the table
  // groups read the reported windows, mirroring the full-page tables.
  const { states, registerState, getGroupProps } = useGroupStates<IssueRow>();
  const groups = useMemo<DataTableGroupType<IssueRow>[]>(
    () =>
      groupDefs.map((def) => ({
        key: def.key,
        label: def.label,
        totalCount: states.get(def.key)?.total ?? (dimension ? def.totalCount : undefined),
        ...getGroupProps(def.key),
      })),
    [groupDefs, dimension, states, getGroupProps],
  );
  const tableLoading = dimension
    ? !groupCounts.ready
    : (states.get("all")?.loading ?? true);

  const renderGroupTitle = useCallback(
    (groupKey: string) => {
      const m = metaByKey[groupKey];
      if (!m) return groupKey;
      if (m.kind === "unassigned") {
        return (
          <Text size="sm" variant="muted">
            {m.label}
          </Text>
        );
      }
      if (m.kind === "stage") {
        return (
          <Box direction="row" align="center" gap="sm" overflow="hidden">
            <EntityTag
              entity={{ kind: "stage", id: m.stage.stageId, stage: m.stage }}
              interactive={false}
            />
          </Box>
        );
      }
      if (m.kind === "assignee") {
        return (
          <Box direction="row" align="center" gap="sm" overflow="hidden">
            <EntityTag entity={m.entity} interactive={false} />
          </Box>
        );
      }
      const ageInfo = AGE_STATUS_CONFIG[m.status];
      return (
        <Box direction="row" align="center" gap="sm" overflow="hidden">
          {/* `decoColor` — the palette hue the age chart slices paint, so the
              chip pulse matches the slice it drills from. */}
          <Icon name={ageInfo.icon} size="md" color={ageInfo.decoColor} />
          <Box fill="flex" shrink>
            <Text size="sm" weight="medium" truncate={1}>
              {ageInfo.label}
            </Text>
          </Box>
        </Box>
      );
    },
    [metaByKey],
  );

  return (
    // No static gap under the toolbar — the table's own `gutterTop` carries
    // the 16px, as scroll content rows pass through.
    <Box direction="column" fill="flex" overflow="hidden">
      {groupDefs.map((def) => (
        <GroupQueryProvider<IssueRow>
          key={def.key}
          groupKey={def.key}
          document={IssuesDocument}
          variables={{ where: def.where, orderBy, limit: PAGE_LIMIT }}
          getConnection={getIssuesConnection}
          onStateChange={registerState}
        />
      ))}
      <DataTableToolbar>
        <DataTableToolbarLeft>
          <DataTableGroupByDropdown
            groupByField={groupByField}
            setGroupByField={setGroupByField}
            groupByLabels={groupByLabels}
            groupByIcons={groupByIcons}
            groupSortDirection={groupSortDirection}
            setGroupSortDirection={setGroupSortDirection}
            iconOnly
          />
          {slice ? (
            <FilterToggle
              size="xs"
              icon="filter"
              label={slice.label}
              selected
              onClear={onClearSlice}
              tooltip={`Filtering by: ${slice.label}`}
              clearTooltip="Clear filter"
            />
          ) : null}
        </DataTableToolbarLeft>
        <DataTableToolbarActions>
          <ExpandableSearch
            value={search}
            onValueChange={setSearch}
            size="xs"
            tooltip="Search in your view"
          />
        </DataTableToolbarActions>
      </DataTableToolbar>

      <Box direction="column" fill="flex" overflow="hidden">
        <DataTable
          columns={columns}
          groups={groups}
          getRowId={(row) => row.issueId}
          loading={tableLoading}
          sorting={sorting}
          onSortingChange={setSorting}
          stickyHeader
          gutterTop="md"
          density="regular"
          paddingX="md"
          paddingXAlign="content"
          surface={panelSurface}
          groupTitle={isGrouped ? renderGroupTitle : undefined}
          groupActions={renderDefaultGroupActions}
          bulkActions={bulkActions}
          bulkSelectionNoun={PROJECT_SELECTION_NOUN}
          canSelectRow={(issue) => issue.viewerCanEdit}
          pinnedColumnIds={PINNED_COLUMN_IDS}
          // On touch a row is a tap to open and nothing else, as in the list
          // view and the widget card's table: the project view carries the
          // row's actions.
          pressOnlyOnTouch
          {...tableView.tableProps}
          // Mounts the circle-plus show/hide picker on the header; the header
          // menu's "Hide column" writes through the same handler.
          onColumnVisibilityChange={tableView.settingsProps.onColumnVisibilityChange}
        />
      </Box>

      {ageDialogIssue && (
        <UpdateAgeDialog
          via="widget_data"
          open
          onOpenChange={(open) => {
            if (!open) setAgeDialogIssue(null);
          }}
          issueId={ageDialogIssue.issueId}
          age={ageDialogIssue.age}
          stageId={ageDialogIssue.stageId}
        />
      )}
    </Box>
  );
}
